import type { AppState } from '@/core/state';
import type { BossDef, Question, Quest, QuestMetric, SkillView, SubjectId } from '@/core/types';
import { ACHIEVEMENTS, BOSSES, LEVEL_TITLES, LEVEL_UNLOCKS, xpToReach } from '@/data/progression';
import { catalog } from '@/data/catalog';
import { DAY, dayKey, startOfDay, uid } from '@/core/util/misc';
import { isRelevant } from './views';
import { L, LL } from '@/i18n';

/* ------------------------------------------------------------------ */
/* Levels & XP                                                          */
/* ------------------------------------------------------------------ */

export function levelInfo(total: number) {
  let level = 1;
  while (xpToReach(level + 1) <= total) level++;
  const base = xpToReach(level);
  const next = xpToReach(level + 1);
  return {
    level,
    title: LEVEL_TITLES[Math.min(level - 1, LEVEL_TITLES.length - 1)],
    into: total - base,
    needed: next - base,
    progress: (total - base) / (next - base),
    nextUnlock: Object.entries(LEVEL_UNLOCKS).map(([l, u]) => ({ level: Number(l), ...u })).find((u) => u.level > level),
  };
}

/**
 * XP reflects learning, not clicking: correct answers near or above the
 * student's edge earn the most; trivial tasks far below it earn little;
 * repeated identical questions earn nothing.
 */
export function xpForAnswer(q: Question, correct: boolean, ability: number | null, hints: number, source: string, repeat: boolean): { amount: number; reason: string } {
  if (repeat) return { amount: 0, reason: L('Repeated question — no XP', 'Gjentatt spørsmål — ingen XP') };
  if (source === 'diagnostic') return { amount: 3, reason: LL('Level check answer', 'Svar i nivåsjekken', { detailed: ['Calibration evidence', 'Kartleggingssvar'] }) };
  if (!correct) {
    const stretch = ability !== null && q.difficulty >= ability + 1;
    return stretch ? { amount: 1, reason: LL('Good effort on a hard task', 'Godt forsøk på en vanskelig oppgave', { detailed: ['Productive struggle above your edge', 'Nyttig strev over nivået ditt'] }) } : { amount: 0, reason: '' };
  }
  let amount = 4 + 2 * q.difficulty;
  let reason = L(`Correct at difficulty ${q.difficulty}`, `Riktig på vanskegrad ${q.difficulty}`);
  if (ability !== null) {
    if (q.difficulty >= ability + 0.5) {
      amount *= 1.5;
      reason = LL(`Hard task solved (difficulty ${q.difficulty}, above your level)`, `Vanskelig oppgave løst (vanskegrad ${q.difficulty}, over nivået ditt)`, { detailed: [`Stretch task solved (difficulty ${q.difficulty} above your estimate)`, `Krevende oppgave løst (vanskegrad ${q.difficulty}, over anslaget ditt)`] });
    } else if (q.difficulty <= ability - 2) {
      amount *= 0.3;
      reason = LL('Too easy for you — little to learn', 'For lett for deg — lite å lære', { detailed: ['Below your edge — little new learning', 'Under nivået ditt — lite ny læring'] });
    }
  }
  if (hints) amount *= Math.max(0.25, 1 - 0.25 * hints);
  return { amount: Math.max(1, Math.round(amount)), reason };
}

/* ------------------------------------------------------------------ */
/* Quests                                                               */
/* ------------------------------------------------------------------ */

export function generateQuests(state: AppState, views: Record<string, SkillView>, now: number): Quest[] {
  const out: Quest[] = [];
  const endOfDay = startOfDay(now) + DAY;
  const relevant = Object.values(views).filter(isRelevant);
  const due = relevant.filter((v) => v.evidenceCount > 0 && v.due !== null && v.due <= now);
  const open = state.mistakes.filter((m) => m.status !== 'resolved');
  const unknownCore = relevant.filter((v) => v.status === 'UNKNOWN' && v.relevance === 'core');
  const gaps = relevant.filter((v) => v.status === 'WEAK' && catalog.dependents(v.skillId).length > 0);
  const q = (cadence: Quest['cadence'], metric: QuestMetric, target: number, title: string, description: string, rewardXp: number, rationale: string, expiresAt?: number): Quest => ({
    id: uid('quest'), title, description, cadence, metric, target, progress: 0, rewardXp, createdAt: now, expiresAt, rationale,
  });
  const t = (metric: QuestMetric, target: number) => questText(metric, target);
  if (due.length) out.push(q('daily', 'reviews-completed', Math.min(5, due.length), t('reviews-completed', Math.min(5, due.length)).title, t('reviews-completed', Math.min(5, due.length)).description, 40, LL(`${due.length} skill${due.length > 1 ? 's' : ''} to review — you're starting to forget.`, `${due.length} ferdighet${due.length > 1 ? 'er' : ''} å repetere — du begynner å glemme.`, { detailed: [`${due.length} skill${due.length > 1 ? 's are' : ' is'} due — recall risk is rising.`, `${due.length} ferdighet${due.length > 1 ? 'er' : ''} bør repeteres nå.`] }), endOfDay));
  if (open.length) out.push(q('daily', 'mistakes-resolved', 1, t('mistakes-resolved', 1).title, t('mistakes-resolved', 1).description, 45, LL(`${open.length} mistake${open.length > 1 ? 's' : ''} waiting for a rematch.`, `${open.length} feil venter på revansj.`, { detailed: [`${open.length} open mistake${open.length > 1 ? 's' : ''} in your Mistake Bank.`, `${open.length} åpne feil i feilbanken din.`] }), endOfDay));
  if (gaps.length) out.push(q('daily', 'prerequisite-recovered', 1, t('prerequisite-recovered', 1).title, t('prerequisite-recovered', 1).description, 40, LL(`${gaps.length} missing piece${gaps.length > 1 ? 's block' : ' blocks'} later skills.`, `${gaps.length} manglende brikker stopper senere ferdigheter.`, { detailed: [`${gaps.length} weak prerequisite${gaps.length > 1 ? 's block' : ' blocks'} later skills.`, `${gaps.length} svake forkunnskaper stopper senere ferdigheter.`] }), endOfDay));
  out.push(q('daily', 'stretch-correct', 3, t('stretch-correct', 3).title, t('stretch-correct', 3).description, 50, LL('You learn most right at the edge of what you can do.', 'Du lærer mest i kanten av det du kan.', { detailed: ['Learning happens at the edge of ability.', 'Læring skjer i kanten av det du kan.'] }), endOfDay));
  if (unknownCore.length) out.push(q('daily', 'new-skill-assessed', 2, t('new-skill-assessed', 2).title, t('new-skill-assessed', 2).description, 35, LL(`${unknownCore.length} key skills are still untried.`, `${unknownCore.length} viktige ferdigheter er ikke prøvd ennå.`, { detailed: [`${unknownCore.length} core skills have no evidence yet.`, `${unknownCore.length} kjerneferdigheter er ikke prøvd ennå.`] }), endOfDay));
  const daily = out.slice(0, 3);
  const weekEnd = startOfDay(now) + 7 * DAY;
  const weekly: Quest[] = [q('weekly', 'skills-mastered', 1, t('skills-mastered', 1).title, t('skills-mastered', 1).description, 120, LL('To master a skill, solve tasks at its mastery level.', 'For å mestre en ferdighet må du løse oppgaver på mestringsnivået.', { detailed: ['Mastery requires solving tasks at the mastery target.', 'Mestring krever at du løser oppgaver på mestringsnivået.'] }), weekEnd)];
  if (readyBosses(state, views).length) weekly.push(q('weekly', 'boss-defeated', 1, t('boss-defeated', 1).title, t('boss-defeated', 1).description, 150, LL('You\'re ready for a boss battle!', 'Du er klar for en bosskamp!', { detailed: ['You have reached boss readiness.', 'Du er klar for en boss.'] }), weekEnd));
  else weekly.push(q('weekly', 'feynman-completed', 2, t('feynman-completed', 2).title, t('feynman-completed', 2).description, 90, LL('Explaining something shows what you really know.', 'Når du forklarer noe, ser du hva du egentlig kan.', { detailed: ['Explaining reveals hidden gaps.', 'Å forklare avslører skjulte hull.'] }), weekEnd));
  return [...daily, ...weekly];
}

/** Quest title/description in the active language (stored text may be from another language). */
export function questText(metric: QuestMetric, target: number): { title: string; description: string } {
  const m: Partial<Record<QuestMetric, [string, string, string, string]>> = {
    'reviews-completed': ['Hold the line', `Complete ${target} due reviews.`, 'Hold stillingen', `Fullfør ${target} repetisjoner.`],
    'mistakes-resolved': ['Repair protocol', 'Resolve one mistake through recovery and re-test.', 'Reparasjon', 'Rett opp én feil gjennom øving og ny test.'],
    'prerequisite-recovered': ['Shore up a foundation', 'Answer a weak prerequisite skill correctly at difficulty 3+.', 'Styrk grunnmuren', 'Svar riktig på en svak forkunnskap på vanskegrad 3 eller høyere.'],
    'stretch-correct': ['Walk the edge', `Solve ${target} tasks above your current estimate.`, 'Gå på kanten', `Løs ${target} oppgaver over nivået ditt.`],
    'new-skill-assessed': ['Chart the unknown', `Give Studio first evidence on ${target} unknown core skills.`, 'Kartlegg det ukjente', `Prøv ${target} kjerneferdigheter du ikke har prøvd før.`],
    'skills-mastered': ['Ascension', 'Bring one skill to MASTERED.', 'Oppstigning', 'Få én ferdighet til MESTRET.'],
    'boss-defeated': ['Gatebreaker', 'Defeat a boss whose gate is open.', 'Portknuser', 'Beseir en boss med åpen port.'],
    'feynman-completed': ['Teach it back', `Pass ${target} Feynman explanations.`, 'Lær det bort', `Bestå ${target} Feynman-forklaringer.`],
  };
  const v = m[metric];
  if (!v) return { title: metric, description: '' };
  return { title: L(v[0], v[2]), description: L(v[1], v[3]) };
}

export function refreshQuests(state: AppState, views: Record<string, SkillView>, now: number): Quest[] | null {
  const today = dayKey(now);
  const active = state.quests.filter((q) => !q.expiresAt || q.expiresAt > now);
  const hasDaily = active.some((q) => q.cadence === 'daily' && dayKey(q.createdAt) === today);
  const hasWeekly = active.some((q) => q.cadence === 'weekly');
  if (hasDaily && hasWeekly) return null;
  const fresh = generateQuests(state, views, now);
  const keep = active.filter((q) => (q.cadence === 'weekly' && hasWeekly) || (q.cadence === 'daily' && hasDaily) || q.cadence === 'story');
  const add = fresh.filter((q) => (q.cadence === 'daily' && !hasDaily) || (q.cadence === 'weekly' && !hasWeekly));
  const history = state.quests.filter((q) => q.completedAt && !keep.includes(q)).slice(-20);
  return [...history, ...keep, ...add];
}

/* ------------------------------------------------------------------ */
/* Bosses                                                               */
/* ------------------------------------------------------------------ */

export function bossReadiness(boss: BossDef, views: Record<string, SkillView>) {
  const vs = boss.skillIds.map((id) => views[id]).filter(Boolean);
  const unknown = vs.filter((v) => v.evidenceCount === 0);
  const meanMastery = vs.reduce((s, v) => s + v.mastery, 0) / Math.max(1, vs.length);
  const ready = unknown.length === 0 && meanMastery >= boss.readiness;
  return { ready, meanMastery, unknown: unknown.map((v) => v.skillId), relevant: vs.some(isRelevant) };
}

export function readyBosses(state: AppState, views: Record<string, SkillView>): BossDef[] {
  const subjects = new Set<SubjectId>(state.profile?.subjects ?? []);
  return BOSSES.filter((b) => subjects.has(b.subjectId) && !state.bosses[b.id]?.defeatedAt && bossReadiness(b, views).ready);
}

/* ------------------------------------------------------------------ */
/* Achievements                                                         */
/* ------------------------------------------------------------------ */

export function checkAchievements(state: AppState, views: Record<string, SkillView>): string[] {
  const have = state.achievements;
  const c = state.counters;
  const out: string[] = [];
  const add = (id: string, cond: boolean) => {
    if (cond && !have[id]) out.push(id);
  };
  const vs = Object.values(views);
  const mastered = vs.filter((v) => v.status === 'MASTERED').length;
  const domains = new Set(vs.filter((v) => v.evidenceCount > 0).map((v) => catalog.skill(v.skillId)!.domainId));
  const langs = new Set(vs.filter((v) => v.evidenceCount > 0).map((v) => catalog.skill(v.skillId)!.subjectId).filter((s) => ['french', 'german', 'polish', 'tagalog'].includes(s)));
  const cal = Object.values(state.estimates).reduce((acc, e) => ({ n: acc.n + e.calibration.n, b: acc.b + e.calibration.brierSum }), { n: 0, b: 0 });
  add('first-evidence', vs.some((v) => v.evidenceCount > 0));
  add('calibrated', Object.keys(state.calibration.summaries).length > 0);
  add('cartographer', domains.size >= 6);
  add('gap-hunter', (c['gap-repaired'] ?? 0) > 0);
  add('comeback', state.mistakes.filter((m) => m.status === 'resolved').length >= 5);
  add('ascendant', mastered >= 1);
  add('five-mastered', mastered >= 5);
  add('deep-diver', (c['d7-independent'] ?? 0) > 0);
  add('edge-walker', (c['stretch-correct'] ?? 0) >= 10);
  add('polyglot', langs.size >= 2);
  add('archivist', state.notes.length >= 5);
  add('architect', state.palaces.some((p) => p.loci.length >= 5));
  add('teacher', state.feynman.filter((f) => f.overall >= 0.7).length >= 3);
  add('boss-slayer', Object.values(state.bosses).some((b) => b.defeatedAt));
  add('streak-7', state.streak.best >= 7);
  add('exam-ready', state.examResults.some((r) => r.scores.reduce((a, b) => a + b, 0) / Math.max(1, r.scores.length) >= 0.8));
  add('calibrated-mind', cal.n >= 20 && cal.b / cal.n < 0.15);
  add('spaced', state.reviewLog.length >= 25);
  add('centurion', (c['correct'] ?? 0) >= 100);
  add('writer', state.writing.length >= 3);
  add('course-complete', state.courses.some((co) => co.completedAt));
  return out;
}

export const achievementById = (id: string) => ACHIEVEMENTS.find((a) => a.id === id);
